import { ChainId } from '@pancakeswap/sdk'
import { ACCESS_RISK_API } from 'config/constants/endpoints'
import type { RiskSummary, TokenRiskReport } from 'server/tokenRisk/types'

export const TOKEN_RISK = {
  UNKNOWN: -1,
  VERY_LOW: 0,
  LOW: 1,
  MEDIUM: 2,
  HIGH: 3,
  VERY_HIGH: 4,
} as const

/**
 * GoPlus summary -> the old numeric level, kept for callers that only ask "is it above MEDIUM?"
 * (ImportToken's "I acknowledge the risk"). The UI renders `report`, never these numbers.
 */
const LEVEL_OF_SUMMARY: Record<RiskSummary, (typeof TOKEN_RISK)[keyof typeof TOKEN_RISK]> = {
  high: TOKEN_RISK.HIGH,
  caution: TOKEN_RISK.MEDIUM,
  clear: TOKEN_RISK.LOW,
  partial: TOKEN_RISK.UNKNOWN,
  no_data: TOKEN_RISK.UNKNOWN,
}

export interface RiskTokenInfo {
  address: string
  chainId: ChainId
  riskLevel: (typeof TOKEN_RISK)[keyof typeof TOKEN_RISK]
  scannedTs: number
  report: TokenRiskReport
}

export const riskInfoFromReport = (report: TokenRiskReport): RiskTokenInfo => ({
  address: report.address,
  chainId: report.chainId,
  riskLevel: report.status === 'ok' ? LEVEL_OF_SUMMARY[report.summary] : TOKEN_RISK.UNKNOWN,
  scannedTs: report.checkedAt ?? 0,
  report,
})

/** Our own API route (/api/risk/56/<address>), which asks GoPlus server side. 503 bodies are reports too. */
export const fetchRiskToken = async (address: string, chainId: number): Promise<RiskTokenInfo> => {
  const response = await fetch(`${ACCESS_RISK_API}/${chainId}/${address}`, { headers: { Accept: 'application/json' } })
  const body = await response.json().catch(() => null)
  if (!body || typeof body.status !== 'string' || typeof body.summary !== 'string') {
    throw new Error(`Token risk check failed (HTTP ${response.status})`)
  }
  return riskInfoFromReport(body as TokenRiskReport)
}
